/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * Clip QA — the second stage of every first pass.
 *
 * WHY THIS EXISTS
 * ---------------
 * The first pass hears the whole recording at once, so its labels are propped
 * up by context: a word it never really resolved still comes back spelled
 * correctly because the sentence around it made the answer obvious. The clips
 * we cut, though, are consumed one at a time — a human in the Verify tab hears
 * 0.4 s of audio and nothing else. A label that only survives with context is
 * a label that will fail in Verify, and a boundary that clipped the first
 * phoneme off a word is invisible until you listen to that word alone.
 *
 * So we ask the model the same question the human will be asked. Each clip goes
 * back to Gemini ON ITS OWN ("what do you hear? no context available"), and the
 * answer is fuzzy-matched against the segment's `heard` label:
 *
 *   1. RECOGNITION      every clip, at its current boundaries.
 *   2. RE-SEGMENT       runs of failures are re-cut as whole speech bursts. This
 *                       is the only stage that can move audio BETWEEN segments,
 *                       and it exists because the first pass distributes labels
 *                       arbitrarily inside a continuous burst — see
 *                       services/legacy/burstResegment.ts.
 *   3. GAP FILL        speech that NO label covers, and bursts whose labels are
 *                       too few to account for their length, are sent back and
 *                       transcribed from scratch. This is the only stage that
 *                       can add a word the first pass never listed — see
 *                       services/legacy/gapFill.ts for why a missing word is heard as
 *                       drift.
 *   4. PADDED RETRIES   failures re-cut at +-0.3 s, then +-0.6 s, clamped so a
 *                       clip can never eat into a neighbour that DID pass.
 *                       A padded clip that is recognised proves the boundary
 *                       was too tight, so its window is adopted.
 *   5. MERGE            two adjacent failures may be one word the first pass
 *                       split in half. Cut them as one clip; if Gemini reads
 *                       the pair back, they become a single segment.
 *   6. FLAG             whatever still fails keeps its boundaries but has its
 *                       confidence pinned to <= 0.3, so it stands out as the
 *                       thing to look at first.
 *
 * BEST EFFORT, ALWAYS
 * -------------------
 * QA runs AFTER the segments and clips are already committed, and every API
 * failure degrades to "leave that segment exactly as it was". A dead key, a
 * rate-limit wall or a cancelled task can cost the user the polish; it can
 * never cost them the first pass.
 */

import { reviseSegments, type SegmentRevision } from '../../db';
import { round3 } from '../../lib/num';
import { foldForCompare, isFillerLabel } from '../../lib/labelText';
import type { Segment } from '../../types';
import { sliceToWav } from '../audioSlicer';
import {
  isImplausiblyLong,
  resegmentBursts,
  type ResegmentItem,
  type ResegmentStats,
} from './burstResegment';
import {
  buildDuplicateContext,
  resolveDuplicatesInItems,
  type DuplicateStats,
} from './duplicateResolve';
import { runGapFill, type GapFillStats } from './gapFill';
import {
  GeminiError,
  retimeRegion,
  transcribeClipInIsolation,
  transcribeGapClip,
  type ClipQaAnswer,
} from '../gemini';

/* ------------------------------------------------------------------ */
/* Tunables                                                            */
/* ------------------------------------------------------------------ */

/** Padding rounds, in seconds. Length is the retry-round cap. */
const PAD_ROUNDS = [0.3, 0.6];

/** Clips are re-listened to at this rate: 16 kHz mono keeps each request tiny. */
const QA_SAMPLE_RATE = 16000;

/** Polite ceiling on in-flight QA requests. */
const DEFAULT_CONCURRENCY = 3;

/** Attempts per clip, including the first. Only retryable errors are retried. */
const MAX_ATTEMPTS = 3;

/** Consecutive hard failures after which the whole QA stage gives up. */
const CIRCUIT_BREAK_AFTER = 8;

/**
 * Wall-clock ceiling on the whole QA stage.
 *
 * WHY THERE HAS TO BE ONE
 * -----------------------
 * Nothing else in this file bounds TIME. The circuit breaker counts consecutive
 * *failures*, and a call that eventually succeeds resets it — so an API that is
 * merely slow trips nothing. The per-call timeouts (60 s a clip, 90 s a region)
 * bound one request, and `MAX_ATTEMPTS` bounds the retries of one request, but
 * the stage issues hundreds of requests and several of its phases are strictly
 * sequential (re-segmentation's per-region re-QA, and the merge pass).
 *
 * Multiply those bounds out on a 52-segment task and the worst case is on the
 * order of six hours with no bug at all: 52 clips x 3 attempts x 60 s over 3
 * workers, then up to 30 regions x 3 x 90 s plus a sequential isolation call per
 * rebuilt segment, then two padded rounds over every failure, then the merges.
 * A run that sat in QA for ~2 h was living inside that envelope — the ceiling
 * was simply never expressed anywhere. It is expressed here.
 *
 * Exceeding it is not an error: QA is polish over an already-committed first
 * pass, so the remaining segments keep the confidence they came in with and the
 * stage records that it stopped early.
 */
const DEFAULT_BUDGET_MS = 5 * 60 * 1000;

/**
 * Wall-clock held back from re-segmentation so gap fill always gets a turn.
 *
 * The budget is one pot shared by every stage in this file, and the stages run
 * in a fixed order, so the last one in the queue is the one that never runs. On
 * the calibration recording QA reached burst re-segmentation with 60 s of a
 * 240 s budget left and re-segmentation used all of it — which was fine while
 * it was the last expensive stage and is not fine now that transcript
 * completeness sits behind it. Completeness is the more valuable of the two
 * (re-segmentation redistributes labels that exist; gap fill supplies labels
 * that do not), so it gets a floor rather than the leftovers.
 *
 * Only applied when the budget is comfortably larger than the reserve, so a
 * caller who deliberately sets a tiny budget still gets stages in order rather
 * than a re-segmentation pass with no time at all.
 */
const GAP_FILL_RESERVE_MS = 75 * 1000;

/**
 * Longest combined span two segments may be merged across. A merge exists to
 * reunite one word the first pass cut in half; a pair spanning more than this
 * is two real events with a pause between them, and gluing them together would
 * bury a whole silence inside a clip labelled as a single word.
 */
const MAX_MERGE_SEC = 1.5;

/** Confidence ceiling applied to clips that never got recognised. */
export const QA_FAILED_CONFIDENCE = 0.3;

/* ------------------------------------------------------------------ */
/* Fuzzy matching                                                      */
/* ------------------------------------------------------------------ */

/*
 * `foldForCompare` and `isFillerLabel` used to be declared here as
 * `normalizeLabel` and `isFillerLabel`. They moved to `src/lib/labelText.ts`
 * for two reasons:
 *
 *  - `duplicateResolve` imported both from this module while this module
 *    imports `duplicateResolve`'s resolver — a real runtime import cycle, both
 *    edges value imports;
 *  - `normalizeLabel` collided by name with `src/model/ids.ts`'s
 *    `normalizeLabel`, which handles punctuation differently and was never
 *    substitutable. `foldForCompare` says what it does and de-collides it.
 *
 * The bodies, the fold table and the token set moved verbatim.
 */

/** Classic Levenshtein distance, two-row variant. */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    const swap = prev;
    prev = curr;
    curr = swap;
  }
  return prev[b.length];
}

/** How many edits we forgive at a given word length. */
function tolerance(len: number): number {
  if (len <= 2) return 0;
  if (len <= 4) return 1;
  if (len <= 7) return 2;
  return 3;
}

function nearlyEqual(a: string, b: string): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return levenshtein(a, b) <= tolerance(Math.max(a.length, b.length));
}

/**
 * Longest run of `expected` tokens that appears in `actual` IN ORDER.
 *
 * Plain LCS with {@link nearlyEqual} standing in for equality, so a token may be
 * misspelt but may not be out of sequence: "انا اسمي امجد" read back as
 * "امجد اسمي انا" is a different utterance, not the same one spelt differently.
 */
function orderedTokenMatches(expected: readonly string[], actual: readonly string[]): number {
  const n = expected.length;
  const m = actual.length;
  if (n === 0 || m === 0) return 0;
  let prev = new Array<number>(m + 1).fill(0);
  let cur = new Array<number>(m + 1).fill(0);
  for (let i = 1; i <= n; i++) {
    cur[0] = 0;
    for (let j = 1; j <= m; j++) {
      cur[j] = nearlyEqual(expected[i - 1], actual[j - 1])
        ? prev[j - 1] + 1
        : Math.max(prev[j], cur[j - 1]);
    }
    const swap = prev;
    prev = cur;
    cur = swap;
  }
  return prev[m];
}

/**
 * Do two labels describe the same sound? Tolerant of spelling, diacritics,
 * hesitation spelling, and of either side being a PHRASE.
 *
 * Multi-token labels are first-class since burst-first grouping: a segment's
 * label is the words of a group joined with spaces (see services/legacy/burstGroups.ts),
 * so QA routinely compares a sequence against a sequence. The rule for that case
 * is deliberately not "every token must land":
 *
 *  - fillers inside a group are free. A hesitation fused into "اه بدي" is the
 *    part of the label a model asked to transcribe a clip is least likely to
 *    write down, and its absence says nothing about whether the clip is right.
 *  - of the remaining tokens, two thirds (rounded up) must appear IN ORDER.
 *    Requiring all of them would fail a correct three-word clip because the
 *    model swallowed one unstressed function word — and the action QA takes on a
 *    failure is to widen or merge the clip, which is precisely the wrong repair
 *    when the boundaries came from silence and a confident onset. Requiring
 *    fewer would pass a clip on the strength of one word it shares with its
 *    neighbour.
 *
 * Single-token behaviour is unchanged, on purpose: it is the case the tolerance
 * table and the whole existing calibration were built for.
 */
export function labelsMatch(expected: string, actual: string): boolean {
  const a = foldForCompare(expected);
  const b = foldForCompare(actual);
  if (!a || !b) return false;
  if (a === b) return true;
  if (isFillerLabel(a) && isFillerLabel(b)) return true;
  if (nearlyEqual(a, b)) return true;

  const ta = a.split(' ').filter(Boolean);
  const tb = b.split(' ').filter(Boolean);

  // The expected label is ONE word and the model answered with a phrase: accept
  // if the word is somewhere in it. Unchanged, and the only rule for this case.
  if (ta.length <= 1) return tb.length > 1 && tb.some((t) => nearlyEqual(a, t));

  /*
   * The expected label is a GROUP. Two thirds of its non-filler tokens must come
   * back, in order — including when the model answered with a single word, which
   * the old rule accepted outright. That leniency was harmless while a multi-word
   * `expected` meant a rare model phrase; now that it is the normal shape of a
   * label, "the clip contains one of the three words" is exactly the evidence
   * that must NOT pass, because it is equally true of the clip next door.
   */
  const core = ta.filter((t) => !isFillerLabel(t));
  if (core.length === 0) return tb.some((t) => isFillerLabel(t));
  const need = Math.max(1, Math.ceil((core.length * 2) / 3));
  return orderedTokenMatches(core, tb) >= need;
}

/**
 * Did the model read BOTH labels back out of one combined clip? That is the
 * evidence required to merge two segments into one.
 */
export function pairMatches(first: string, second: string, answer: string): boolean {
  const a = foldForCompare(first);
  const b = foldForCompare(second);
  const ans = foldForCompare(answer);
  if (!a || !b || !ans) return false;

  /*
   * Cut the answer in two and require the left half to be the first label and
   * the right half the second. Written as a cut rather than as "find token i for
   * A, then token j > i for B" because either side can now be a multi-word group
   * label, and a group is only merged into its neighbour when the WHOLE of it
   * reads back before the whole of the other one.
   */
  const toks = ans.split(' ').filter(Boolean);
  for (let cut = 1; cut < toks.length; cut++) {
    if (!labelsMatch(a, toks.slice(0, cut).join(' '))) continue;
    if (labelsMatch(b, toks.slice(cut).join(' '))) return true;
  }
  // The halves may have been read back as one token ("kita" + "b" -> "kitab").
  // Split the answer at every position and require BOTH pieces to land: a plain
  // `nearlyEqual(a + b, ans)` would merge a pair on the strength of one half
  // alone, since dropping a short first half ("uh" + "hello" -> "hello") costs
  // fewer edits than the tolerance allows.
  const flat = ans.replace(/ /g, '');
  for (let k = 1; k < flat.length; k++) {
    if (nearlyEqual(a, flat.slice(0, k)) && nearlyEqual(b, flat.slice(k))) return true;
  }
  return false;
}

/**
 * Did this clip pass QA?
 *
 * Words are judged on their text. Non-word segments are judged on their
 * category instead, because "door slam" and "keyboard click" are both correct
 * descriptions of the same noise and no string comparison can know that.
 */
export function qaPasses(segment: Pick<Segment, 'heard' | 'kind'>, answer: ClipQaAnswer): boolean {
  if (labelsMatch(segment.heard, answer.text)) return true;
  switch (segment.kind) {
    case 'noise':
      return answer.kind === 'noise';
    case 'unclear':
      return answer.kind === 'unclear' || answer.kind === 'noise';
    case 'filler':
      return answer.kind === 'filler' || isFillerLabel(answer.text);
    case 'silence':
      // Silence tiles are cut from VAD-quiet spans; if the model heard a real
      // word in one, that IS a failure worth surfacing.
      return answer.kind === 'noise' || answer.text.trim() === '';
    case 'word':
      return false;
  }
}

/* ------------------------------------------------------------------ */
/* Scheduling: small pool, backoff, circuit breaker                    */
/* ------------------------------------------------------------------ */

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms);
    function done(): void {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    }
    signal?.addEventListener('abort', done);
  });
}

/** Run `fn` over `items` with at most `limit` in flight, preserving order. */
async function pool<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      out[i] = await fn(items[i], i);
    }
  });
  await Promise.all(workers);
  return out;
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export interface ClipQaOptions {
  signal?: AbortSignal;
  /** Padding the stored clips were cut with, so QA hears the same audio. */
  padSec?: number;
  /** Max concurrent Gemini requests. Default 3. */
  concurrency?: number;
  /** Wall-clock ceiling for the whole stage, ms. Default 4 minutes. 0 = none. */
  budgetMs?: number;
  /**
   * Every boundary already sits on silence or a confident acoustic onset.
   *
   * Set by the first pass when burst-first grouping ran. It turns the padded
   * retries off, because under grouping they are not merely wasteful but wrong:
   * padding exists to rescue a clip cut too tight, and a clip whose edges are a
   * silence gap and a spectral onset is not cut too tight — widening it only
   * adds the neighbouring group's audio until the model recognises SOMETHING,
   * and then adopts that window, which overlaps a neighbour and destroys the
   * gapless tiling of the burst that grouping guarantees. The calls saved go to
   * gap fill, which is the stage that can still find a genuinely missing word.
   */
  structuralBoundaries?: boolean;
  /** Called after every clip check. */
  onProgress?: (info: { phase: ClipQaStage; done: number; total: number }) => void;
}

export type ClipQaStage = 'recognising' | 'resegmenting' | 'gapfilling' | 'repadding' | 'merging';

/** What the QA loop did. Every count refers to segments, not requests. */
export interface ClipQaStats {
  /** Segments that entered QA. */
  total: number;
  /** Segments the task ends up with — re-segmentation adds and drops rows. */
  finalCount: number;
  /** Recognised at their original boundaries, first try. */
  passedFirstTry: number;
  /** What burst re-segmentation did, when it ran. */
  resegment?: ResegmentStats;
  /** What the transcript-completeness pass did, when it ran. */
  gapFill?: GapFillStats;
  /** What the duplicate resolver flagged after it, when it ran. */
  duplicates?: DuplicateStats;
  /** Recognised only after re-cutting wider; boundaries were adopted. */
  repairedByPadding: number;
  /** True when the padded rounds were skipped; see `structuralBoundaries`. */
  paddingSkipped?: boolean;
  /** Absorbed into a neighbour because the pair read back as one utterance. */
  repairedByMerge: number;
  /** Never recognised; confidence pinned to {@link QA_FAILED_CONFIDENCE}. */
  flaggedLowConfidence: number;
  /** Segments whose QA request never succeeded — left untouched. */
  skipped: number;
  /** Gemini requests issued, retries included. */
  apiCalls: number;
  /** True when the circuit breaker tripped and QA stopped early. */
  abandoned: boolean;
  /** True when the wall-clock budget ran out before QA finished. */
  budgetExceeded: boolean;
  /** Clip checks never made because the budget had run out. */
  skippedByBudget: number;
  /** How long the stage actually took, ms. */
  elapsedMs: number;
}

/**
 * One segment's state through the QA loop. Defined in `burstResegment.ts`
 * because the re-segmentation stage splices this list in place.
 */
type QaItem = ResegmentItem;

/**
 * Re-listen to every clip of a task in isolation, repair what can be repaired
 * and flag what cannot. Returns what it did; never throws for an API problem.
 */
export async function runClipQa(
  buffer: AudioBuffer,
  segments: Segment[],
  options: ClipQaOptions = {},
): Promise<ClipQaStats> {
  const { signal, padSec, onProgress } = options;
  const concurrency = Math.max(1, options.concurrency ?? DEFAULT_CONCURRENCY);
  const startedAt = Date.now();
  const budgetMs = options.budgetMs ?? DEFAULT_BUDGET_MS;
  const deadlineAt = budgetMs > 0 ? startedAt + budgetMs : undefined;
  const outOfTime = (): boolean => deadlineAt !== undefined && Date.now() > deadlineAt;
  // See GAP_FILL_RESERVE_MS: re-segmentation stops early so gap fill can start.
  const resegmentDeadlineAt =
    deadlineAt !== undefined && budgetMs > GAP_FILL_RESERVE_MS * 2
      ? deadlineAt - GAP_FILL_RESERVE_MS
      : deadlineAt;

  const stats: ClipQaStats = {
    total: segments.length,
    finalCount: segments.length,
    passedFirstTry: 0,
    repairedByPadding: 0,
    repairedByMerge: 0,
    flaggedLowConfidence: 0,
    skipped: 0,
    apiCalls: 0,
    abandoned: false,
    budgetExceeded: false,
    skippedByBudget: 0,
    elapsedMs: 0,
  };
  if (segments.length === 0) return stats;

  const items: QaItem[] = segments.map((s) => ({
    seg: { ...s },
    dirty: false,
    clipDirty: false,
    removed: false,
  }));

  let consecutiveErrors = 0;

  /**
   * One Gemini request with backoff, the shared circuit breaker and the call
   * counter. Null means "we gave up on this one" — never "the answer was no".
   */
  async function attempt<T>(run: () => Promise<T>): Promise<T | null> {
    if (signal?.aborted || consecutiveErrors >= CIRCUIT_BREAK_AFTER) return null;
    // The budget is checked HERE rather than only between phases, because a
    // single phase can outlast it on its own: 52 clips at three attempts each
    // is 156 requests, and the pool keeps them coming.
    if (outOfTime()) {
      stats.budgetExceeded = true;
      stats.skippedByBudget++;
      return null;
    }

    for (let tries = 0; tries < MAX_ATTEMPTS; tries++) {
      if (signal?.aborted) return null;
      // A retry after the deadline buys nothing: the answer would arrive for a
      // stage that has already given up on the rest of its work.
      if (tries > 0 && outOfTime()) {
        stats.budgetExceeded = true;
        return null;
      }
      try {
        stats.apiCalls++;
        const answer = await run();
        consecutiveErrors = 0;
        return answer;
      } catch (err) {
        const retryable = err instanceof GeminiError && err.retryable;
        if (!retryable || tries === MAX_ATTEMPTS - 1) {
          consecutiveErrors++;
          return null;
        }
        // 429 / 5xx / a flaky connection: back off and let the pool drain.
        await sleep(700 * 2 ** tries + Math.random() * 400, signal);
      }
    }
    return null;
  }

  /** One QA request for a time window. Null = give up on it. */
  function ask(startSec: number, endSec: number): Promise<ClipQaAnswer | null> {
    const clip = sliceToWav(buffer, startSec, endSec, {
      padSec,
      targetSampleRate: QA_SAMPLE_RATE,
    });
    return attempt(() => transcribeClipInIsolation({ audio: clip, signal }));
  }

  /* -- 1. recognition ------------------------------------------------ */
  let done = 0;
  const report = (phase: ClipQaStage, total: number) => {
    onProgress?.({ phase, done: ++done, total });
  };

  await pool(items, concurrency, async (item) => {
    const answer = await ask(item.seg.startSec, item.seg.endSec);
    if (answer) item.passed = qaPasses(item.seg, answer);
    report('recognising', items.length);
  });

  stats.passedFirstTry = items.filter((i) => i.passed === true).length;

  /* -- 2. burst re-segmentation --------------------------------------- */
  /*
   * Runs before the padded retries on purpose. Padding can only widen ONE
   * segment into the slack around it; when a whole burst was mis-divided the
   * slack does not exist, and every later stage is working on a segmentation
   * that cannot be repaired one clip at a time. Re-cutting first means padding
   * and merging get to operate on plausible segments — and on whatever
   * re-segmentation left behind, since it splices its results into `items`.
   */
  if (!signal?.aborted && consecutiveErrors < CIRCUIT_BREAK_AFTER && !outOfTime()) {
    done = 0;
    try {
      stats.resegment = await resegmentBursts(items, {
        buffer,
        padSec,
        signal,
        deadlineAt: resegmentDeadlineAt,
        askRegion: (startSec, endSec, labels, clip, speech) =>
          attempt(() =>
            retimeRegion({
              audio: clip,
              clipDurationSec: endSec - startSec,
              labels,
              speechStartSec: speech.startSec,
              speechEndSec: speech.endSec,
              signal,
            }),
          ),
        askClip: ask,
        passes: qaPasses,
        onProgress: (regionDone, total) => {
          done = regionDone;
          onProgress?.({ phase: 'resegmenting', done: regionDone, total });
        },
      });
    } catch (err) {
      // The regions are persisted one at a time, so anything thrown here leaves
      // an already-coherent task; the rest of QA can carry on regardless.
      console.warn('[lableforge] burst re-segmentation failed, keeping the segmentation:', err);
    }
  }

  /* -- 3. transcript completeness (gap fill) --------------------------- */
  /*
   * After re-segmentation, deliberately. Re-segmentation is the stage that both
   * fixes and CREATES bare stretches of audio: it re-times a burst so its labels
   * stop swallowing their neighbours (freeing real speech that nothing now
   * covers) and it deletes crams whose words were found elsewhere (measured on
   * the calibration file: five deleted rows left 0.32 s of speech bare). Running
   * the gap search before it would measure a segmentation that is about to
   * change, and would then race it for the same audio.
   *
   * Before the padded retries, equally deliberately: an inserted word is a new
   * failing neighbour for the segments around it, and padding, merging and
   * flagging should all see it. `runGapFill` splices its results into `items`.
   */
  if (!signal?.aborted && consecutiveErrors < CIRCUIT_BREAK_AFTER && !outOfTime()) {
    done = 0;
    try {
      stats.gapFill = await runGapFill(items, {
        buffer,
        padSec,
        signal,
        deadlineAt,
        askGap: (startSec, endSec, knownLabels, clip, speech) =>
          attempt(() =>
            transcribeGapClip({
              audio: clip,
              clipDurationSec: endSec - startSec,
              knownLabels,
              speechStartSec: speech.startSec,
              speechEndSec: speech.endSec,
              signal,
            }),
          ),
        askClip: ask,
        passes: qaPasses,
        matches: labelsMatch,
        onProgress: (gapDone, total) => {
          done = gapDone;
          onProgress?.({ phase: 'gapfilling', done: gapDone, total });
        },
      });
    } catch (err) {
      // Regions are persisted one at a time, so anything thrown here leaves an
      // already-coherent task; the rest of QA carries on regardless.
      console.warn('[lableforge] gap fill failed, keeping the transcript as-is:', err);
    }
  }

  /* -- 3b. duplicate resolution --------------------------------------- */
  /*
   * Immediately after gap fill, and costing nothing at the API: gap fill is the
   * stage that CREATES duplicates (it inserts the word it heard and, by policy,
   * never removes the stale copy of it that was already there), so this is the
   * one point in the pipeline where both copies are on the list and the audio
   * evidence for each is still to hand. Nothing is deleted here — the losing
   * copy is marked as a suggested removal for the Correct queue; see
   * services/legacy/duplicateResolve.ts. Purely local DSP, so it runs even when the
   * budget is gone: skipping it would leave the duplicates gap fill just made.
   */
  const insertedAnything =
    (stats.gapFill?.segmentsInserted ?? 0) + (stats.resegment?.segmentsInserted ?? 0) > 0;
  if (!signal?.aborted && insertedAnything) {
    try {
      stats.duplicates = resolveDuplicatesInItems(items, buildDuplicateContext(buffer));
    } catch (err) {
      console.warn('[lableforge] duplicate resolution failed, keeping every label:', err);
    }
  }

  /* -- 4. padded retries --------------------------------------------- */
  // See ClipQaOptions.structuralBoundaries: under burst-first grouping there is
  // no such thing as a clip cut too tight, so there is nothing here to repair.
  for (const pad of options.structuralBoundaries ? [] : PAD_ROUNDS) {
    if (signal?.aborted || consecutiveErrors >= CIRCUIT_BREAK_AFTER) break;
    if (outOfTime()) {
      stats.budgetExceeded = true;
      break;
    }
    // A row the duplicate resolver just flagged is not a boundary problem —
    // widening it would only spend a call proving that its label exists in the
    // audio next door, which is exactly what got it flagged.
    const failing = items.filter((i) => i.passed === false && !i.seg.suggestedRemoval);
    if (failing.length === 0) break;

    done = 0;
    await pool(failing, concurrency, async (item) => {
      const index = items.indexOf(item);
      const from = Math.max(lowerBound(items, index), item.seg.startSec - pad);
      const to = Math.min(upperBound(items, index, buffer.duration), item.seg.endSec + pad);
      // Nothing to gain when the clamps left us with the same window.
      if (to - from <= item.seg.endSec - item.seg.startSec + 1e-3) {
        report('repadding', failing.length);
        return;
      }
      // A window this wide no longer tests the boundary — it just contains the
      // word, along with whatever else is next to it. See `isImplausiblyLong`.
      if (isImplausiblyLong(item.seg, to - from)) {
        report('repadding', failing.length);
        return;
      }
      const answer = await ask(from, to);
      if (answer && qaPasses(item.seg, answer)) {
        item.seg.startSec = round3(from);
        item.seg.endSec = round3(to);
        item.passed = true;
        item.dirty = true;
        item.clipDirty = true;
        stats.repairedByPadding++;
      }
      report('repadding', failing.length);
    });
  }

  /* -- 5. merge adjacent failures ------------------------------------ */
  if (!signal?.aborted && consecutiveErrors < CIRCUIT_BREAK_AFTER && !outOfTime()) {
    const pairs: Array<[QaItem, QaItem]> = [];
    for (let i = 0; i < items.length - 1; i++) {
      // Same reasoning as the padded round: a suggested removal must not be
      // glued into its neighbour, which would bury the flag inside a real row.
      if (items[i].seg.suggestedRemoval || items[i + 1].seg.suggestedRemoval) continue;
      if (items[i].passed === false && items[i + 1].passed === false) {
        pairs.push([items[i], items[i + 1]]);
        i++; // a segment can only be merged into one neighbour
      }
    }

    done = 0;
    // Sequential on purpose: merges rewrite their neighbours, and the pairs are
    // few. The pool's value is in the hundreds of round-1 calls, not here.
    for (const [left, right] of pairs) {
      if (signal?.aborted || consecutiveErrors >= CIRCUIT_BREAK_AFTER) break;
      if (outOfTime()) {
        stats.budgetExceeded = true;
        break;
      }
      if (right.seg.endSec - left.seg.startSec > MAX_MERGE_SEC) continue;
      const answer = await ask(left.seg.startSec, right.seg.endSec);
      report('merging', pairs.length);
      if (!answer) continue;
      if (!pairMatches(left.seg.heard, right.seg.heard, answer.text)) continue;

      left.seg.endSec = right.seg.endSec;
      left.seg.heard = answer.text.trim();
      left.seg.contextual = answer.text.trim();
      left.seg.kind = 'word';
      left.passed = true;
      left.dirty = true;
      left.clipDirty = true;
      right.removed = true;
      stats.repairedByMerge++;
    }
  }

  /* -- 6. flag the survivors ----------------------------------------- */
  for (const item of items) {
    if (item.removed) continue;
    if (item.passed === undefined) {
      stats.skipped++;
      continue;
    }
    if (item.passed) continue;
    if (item.seg.confidence > QA_FAILED_CONFIDENCE) {
      item.seg.confidence = QA_FAILED_CONFIDENCE;
      item.dirty = true;
    }
    stats.flaggedLowConfidence++;
  }
  if (options.structuralBoundaries) stats.paddingSkipped = true;
  stats.abandoned = consecutiveErrors >= CIRCUIT_BREAK_AFTER;
  stats.elapsedMs = Date.now() - startedAt;
  if (stats.budgetExceeded) {
    // Deliberately a warning even in production: a task whose QA was cut short
    // has real segments with unverified confidence, and the user is entitled to
    // know that from the console without opening the run log.
    console.warn(
      `[lableforge] qa-skipped-budget: clip QA stopped after ${(stats.elapsedMs / 1000).toFixed(0)}s ` +
        `(budget ${(budgetMs / 1000).toFixed(0)}s); ${stats.skipped} segments keep their original confidence.`,
    );
  }

  /* -- 7. persist ----------------------------------------------------- */
  const survivors = items.filter((i) => !i.removed);
  stats.finalCount = survivors.length;
  separate(survivors);
  const deleteIds = items.filter((i) => i.removed).map((i) => i.seg.id);
  const revisions: SegmentRevision[] = [];

  survivors.forEach((item, index) => {
    // A merge shortens the list, so everything after it is renumbered.
    if (item.seg.index !== index) {
      item.seg.index = index;
      item.dirty = true;
    }
    if (!item.dirty) return;
    revisions.push(
      item.clipDirty
        ? { segment: item.seg, clip: sliceToWav(buffer, item.seg.startSec, item.seg.endSec, { padSec }) }
        : { segment: item.seg },
    );
  });

  await reviseSegments(revisions, deleteIds);
  // The QA stage is invisible in the UI by design (it only nudges confidence
  // and boundaries), so leave a summary behind for anyone tuning the matcher.
  // Stringified rather than logged as an object: `stats` now nests the
  // re-segmentation counters, and a collapsed object hides exactly the numbers
  // you opened the console for.
  if (import.meta.env.DEV) console.info('[lableforge] clip QA', JSON.stringify(stats));
  return stats;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/**
 * How far left segment `index` may grow: at most halfway into its neighbour.
 *
 * The hard requirement is that a clip may never cross the midpoint of a
 * neighbour that PASSED — that neighbour is real audio with a proven label and
 * eating it would corrupt a good segment to rescue a bad one. We apply the same
 * midpoint rule to a failing neighbour, because the alternative (let it swallow
 * the whole neighbour) produces two segments claiming the same audio. The case
 * that rule gives up on — one word genuinely split across two segments — is
 * what the merge step exists to handle, and a merge yields ONE segment rather
 * than two overlapping ones.
 */
function lowerBound(items: QaItem[], index: number): number {
  for (let i = index - 1; i >= 0; i--) {
    const prev = items[i];
    if (prev.removed) continue;
    return (prev.seg.startSec + prev.seg.endSec) / 2;
  }
  return 0;
}

/** Mirror of {@link lowerBound} for the right-hand side. */
function upperBound(items: QaItem[], index: number, duration: number): number {
  for (let i = index + 1; i < items.length; i++) {
    const next = items[i];
    if (next.removed) continue;
    return (next.seg.startSec + next.seg.endSec) / 2;
  }
  return duration;
}

/**
 * Restore the no-overlap invariant the first pass guarantees.
 *
 * Midpoint clamping bounds how far a padded window can reach but does not stop
 * two NEIGHBOURING windows from both reaching toward each other and meeting in
 * the middle. Overlapping segments would put the same audio in two clips and
 * make an exported dataset self-contradictory, so the last word goes to
 * whichever segment did NOT move: a boundary we widened on a guess yields to
 * one we never touched, and when both moved they split the difference.
 */
function separate(items: QaItem[]): void {
  for (let i = 1; i < items.length; i++) {
    const prev = items[i - 1];
    const cur = items[i];
    if (prev.seg.endSec <= cur.seg.startSec) continue;

    if (prev.clipDirty && cur.clipDirty) {
      const mid = round3((cur.seg.startSec + prev.seg.endSec) / 2);
      prev.seg.endSec = mid;
      cur.seg.startSec = mid;
    } else if (prev.clipDirty) {
      prev.seg.endSec = round3(cur.seg.startSec);
    } else {
      cur.seg.startSec = round3(prev.seg.endSec);
    }
    prev.dirty = true;
    cur.dirty = true;
  }
}
